import { BilledAmountIdsByCode, BillingCodeBilledAmount } from '../types/BillingCode.type';
import { createDecimal } from './createDecimal';

/**
 * "Decomp, $60.00" for a picked billed amount, or "DRX, $85.00" for the own amount of a code
 * with additional ones (no id, but a shortcut); null for a code with one amount.
 */
export const billedAmountLabel = (picked: {
  billedAmountId?: number | null;
  billedAmount?: number | string | null;
  shortcut?: string | null;
}) =>
  picked.billedAmountId || picked.shortcut
    ? `${picked.shortcut}, $${createDecimal(picked.billedAmount ?? 0).toFixed(2)}`
    : null;

/**
 * 13052385647: the billed amount a Plan Wizard selection picked for each of its codes, or
 * undefined when it picked none (every code uses its own amount, as before).
 */
export const selectionBilledAmountIds = ({
  primaryCptCode,
  primaryBilledAmountId,
  secondaryCptCode,
  secondaryBilledAmountId,
}: {
  primaryCptCode?: string | null;
  primaryBilledAmountId?: number | string | null;
  secondaryCptCode?: string | null;
  secondaryBilledAmountId?: number | string | null;
}): BilledAmountIdsByCode | undefined => {
  const ids: BilledAmountIdsByCode = {};
  const primary = (primaryCptCode ?? '').trim();
  const secondary = (secondaryCptCode ?? '').trim();

  if (primary && Number(primaryBilledAmountId) > 0) ids[primary] = Number(primaryBilledAmountId);

  if (secondary && Number(secondaryBilledAmountId) > 0) {
    ids[secondary] = Number(secondaryBilledAmountId);
  }

  return Object.keys(ids).length ? ids : undefined;
};

/**
 * The picked amount of a code while the code still has it; undefined means use the code's
 * own amount (the picked one was deleted, or nothing was picked).
 */
export const pickedBilledAmount = (
  code: { billedAmounts?: Pick<BillingCodeBilledAmount, 'id' | 'billedAmount'>[] | null },
  billedAmountId?: number | string | null
) =>
  Number(billedAmountId) > 0
    ? code.billedAmounts?.find(row => Number(row.id) === Number(billedAmountId))
    : undefined;

/**
 * The code priced at its picked amount while it still has it, remembering which one it was;
 * otherwise unchanged.
 */
export const withPickedPrice = <
  T extends { billedAmounts?: Pick<BillingCodeBilledAmount, 'id' | 'billedAmount'>[] | null },
>(
  code: T,
  billedAmountId?: number | string | null
): T & { billedAmountId?: number } => {
  const picked = pickedBilledAmount(code, billedAmountId);

  if (!picked) return code;

  // The server's two-decimal text ("60.00"): the purchase adds a unit to a line already at
  // this price only when the prices match as text.
  const price = createDecimal(picked.billedAmount).toFixed(2);

  return { ...code, billedAmount: price, amount: price, billedAmountId: picked.id };
};

/**
 * The billed amount a purchase line bills, of the code on it now. While a payor replacement is
 * on the line (G0283 for 97012 Decomp) it keeps the original code's only for Undo, so none.
 */
export const lineBilledAmountId = (line: {
  billedAmountId?: number | null;
  replacementOriginalCode?: string | null;
}) => (line.replacementOriginalCode ? null : line.billedAmountId) || null;

type CodePick = {
  code?: string;
  billedAmountId?: number | null;
  billedAmount?: number | string | null;
  shortcut?: string | null;
};

/** A Plan Wizard button's picks from the record its first two codes come from. */
export const selectionBilledAmountsFromCodes = (codes: CodePick[] | null | undefined) => ({
  primaryBilledAmountId: codes?.[0]?.billedAmountId || null,
  primaryBilledAmountLabel: billedAmountLabel(codes?.[0] ?? {}),
  secondaryBilledAmountId: codes?.[1]?.billedAmountId || null,
  secondaryBilledAmountLabel: billedAmountLabel(codes?.[1] ?? {}),
});
